import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { productGuidesData, getGuideBySlug } from '../data/productGuidesData';

function ProductGuide() {
  const { lensSlug, categorySlug } = useParams();
  const slug = `${lensSlug}/${categorySlug}`;
  const guide = getGuideBySlug(slug);

  if (!guide) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-charcoal mb-4">Guide Not Found</h1>
          <p className="text-gray-500 mb-8">The product guide you're looking for doesn't exist.</p>
          <Link to="/lenses" className="text-sage-600 hover:text-sage-700 font-medium">
            ← Browse Ingredient Lenses
          </Link>
        </div>
      </div>
    );
  }

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": guide.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{guide.metaTitle}</title>
        <meta name="description" content={guide.metaDescription} />
        <meta name="keywords" content={`${guide.categoryLabel} without ${guide.lensLabel.toLowerCase()}, ${guide.lensLabel.toLowerCase()}-free ${guide.categoryLabel.toLowerCase()}, healthy ${guide.categoryLabel.toLowerCase()}`} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={guide.metaTitle} />
        <meta property="og:description" content={guide.metaDescription} />
        <meta property="og:url" content={`https://foodlens-ai.com/${guide.slug}`} />
        <meta property="og:site_name" content="FoodLens AI" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={guide.metaTitle} />
        <meta name="twitter:description" content={guide.metaDescription} />

        {/* Canonical */}
        <link rel="canonical" href={`https://foodlens-ai.com/${guide.slug}`} />

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center space-x-2 text-gray-500">
            <li><Link to="/" className="hover:text-sage-600 transition-colors">Home</Link></li>
            <li><span className="mx-2">/</span></li>
            <li><Link to="/lenses" className="hover:text-sage-600 transition-colors">Lenses</Link></li>
            <li><span className="mx-2">/</span></li>
            <li><Link to={`/lenses/${guide.lensId}`} className="hover:text-sage-600 transition-colors">{guide.lensLabel}</Link></li>
            <li><span className="mx-2">/</span></li>
            <li className="text-charcoal font-medium">{guide.categoryLabel}</li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <h1 className="font-display text-4xl sm:text-5xl text-charcoal mb-4">
            {guide.categoryLabel} Without {guide.lensLabel}
          </h1>
          <p className="text-xl text-gray-600">
            {guide.intro}
          </p>
        </header>

        {/* Product Recommendations */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">
            Best {guide.lensLabel}-Free {guide.categoryLabel} Brands
          </h2>
          <div className="space-y-6">
            {guide.products.map((product, index) => (
              <div key={index} className="p-6 bg-surface border border-sage-100 rounded-2xl hover:shadow-soft transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg text-charcoal">{product.name}</h3>
                  {product.link && (
                    <a
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-sage-600 hover:text-sage-700 font-medium flex items-center"
                    >
                      View on Amazon
                      <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
                <p className="text-gray-600 mb-3">{product.reason}</p>
                <div className="flex flex-wrap gap-2">
                  {product.keyIngredients.map((ingredient, i) => (
                    <span key={i} className="px-3 py-1 bg-sage-50 text-sage-700 text-sm rounded-lg">
                      {ingredient}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What to Look For */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">What to Look For</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-red-50 border border-red-100 rounded-2xl">
              <h3 className="font-semibold text-red-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Ingredients to Avoid
              </h3>
              <ul className="space-y-2">
                {guide.ingredientsToAvoid.map((ingredient, i) => (
                  <li key={i} className="text-red-700 flex items-center">
                    <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                    {ingredient}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 bg-green-50 border border-green-100 rounded-2xl">
              <h3 className="font-semibold text-green-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Safe Alternatives
              </h3>
              <ul className="space-y-2">
                {guide.ingredientsOkay.map((ingredient, i) => (
                  <li key={i} className="text-green-700 flex items-center">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                    {ingredient}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Quick Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-surface rounded-2xl overflow-hidden">
              <thead className="bg-sage-50">
                <tr>
                  <th className="text-left p-4 font-semibold text-charcoal">Brand</th>
                  <th className="text-left p-4 font-semibold text-charcoal">Key Ingredients</th>
                  <th className="text-left p-4 font-semibold text-charcoal">Link</th>
                </tr>
              </thead>
              <tbody>
                {guide.products.map((product, index) => (
                  <tr key={index} className="border-t border-sage-100">
                    <td className="p-4 font-medium text-charcoal">{product.name}</td>
                    <td className="p-4 text-gray-600">{product.keyIngredients.slice(0, 3).join(', ')}</td>
                    <td className="p-4">
                      {product.link ? (
                        <a href={product.link} target="_blank" rel="noopener noreferrer" className="text-sage-600 hover:text-sage-700">
                          Amazon
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {guide.faqs.map((faq, index) => (
              <div key={index} className="p-6 bg-surface border border-sage-100 rounded-2xl">
                <h3 className="font-semibold text-charcoal mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-12 p-8 bg-gradient-sage rounded-2xl text-white">
          <h2 className="font-display text-2xl mb-4">
            Scan Any Product for {guide.lensLabel}
          </h2>
          <p className="text-lg text-white/90 mb-6">
            FoodLens AI instantly identifies {guide.lensLabel.toLowerCase()} and other concerning ingredients. Scan any food label to make informed choices.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/app"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-sage-600 font-medium rounded-xl hover:bg-sage-50 transition-colors"
            >
              Download FoodLens AI
            </Link>
            <Link
              to="/extension"
              className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-medium rounded-xl border border-white/30 hover:bg-white/20 transition-colors"
            >
              Get Browser Extension
            </Link>
          </div>
        </section>

        {/* Related Guides */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Related Guides</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {productGuidesData
              .filter(g => g.slug !== guide.slug && (g.lens === guide.lens || g.category === guide.category))
              .slice(0, 4)
              .map((relatedGuide) => (
                <Link
                  key={relatedGuide.slug}
                  to={`/${relatedGuide.slug}`}
                  className="p-4 bg-surface border border-sage-100 rounded-xl hover:shadow-soft hover:-translate-y-1 transition-all"
                >
                  <h3 className="font-semibold text-charcoal mb-1">
                    {relatedGuide.categoryLabel} Without {relatedGuide.lensLabel}
                  </h3>
                  <p className="text-sm text-gray-500">{relatedGuide.products.length} recommended products</p>
                </Link>
              ))}
          </div>
        </section>

        {/* Back Link */}
        <div className="text-center">
          <Link
            to={`/lenses/${guide.lensId}`}
            className="inline-flex items-center text-sage-600 hover:text-sage-700 font-medium"
          >
            <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Learn More About {guide.lensLabel}
          </Link>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 p-6 bg-sage-50 border-l-4 border-sage-300 rounded-r-xl text-sm text-gray-600">
          <p className="font-semibold mb-2 text-charcoal">Disclaimer</p>
          <p>
            Product information and ingredients may change. Always verify ingredients on the product label before purchasing.
            This guide is for informational purposes only and is not medical advice.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ProductGuide;
